import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Badge } from 'components/ui/badge';
import { cn } from 'lib/utils';
import { formatDay, testCost } from 'modules/studyPlan/labels';
import type { WeekDay, WeekTest } from 'modules/studyPlan';

const linkClass = 'underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Monday to Sunday: what was done, what was missed, today's tests and the ones coming. */
export function WeekCard({ week }: { week: WeekDay[] }) {
  const { t, i18n } = useTranslation();
  const language = i18n?.language ?? 'en';

  const testRow = (test: WeekTest, today: boolean) => {
    const title = test.title ?? t('studyPlan.day_untitled');
    // Free is the usual case and goes unsaid here; a test that costs something says so.
    const cost = test.accessType && test.accessType !== 'FREE' ? <span className="text-muted-foreground"> · {testCost(test, t)}</span> : null;
    if (test.done && test.attemptId) {
      return (
        <li key={`${test.id}:${test.attemptId}`} className="flex items-center gap-1.5">
          <Check size={13} strokeWidth={3} aria-hidden="true" className="shrink-0 text-muted-foreground" />
          <span className="sr-only">{t('studyPlan.day_done')}</span>
          <Link to={`/portal/results/${test.attemptId}`} className={cn('text-foreground/80', linkClass)}>{title}</Link>
        </li>
      );
    }
    return (
      <li key={`${test.id}:plan`}>
        <Link
          to={`/portal/tests/take/${test.id}`}
          className={cn(today ? 'font-semibold text-foreground' : 'text-foreground/90 hover:text-foreground', linkClass)}
        >
          {title}
        </Link>
        {cost}
      </li>
    );
  };

  return (
    <section aria-labelledby="plan-week" className="rounded-lg border border-border bg-card p-5 md:p-6">
      <h2 id="plan-week" className="text-[15px] font-semibold text-foreground">{t('studyPlan.week_title')}</h2>
      <ul className="mt-3">
        {week.map((day) => {
          const today = day.state === 'TODAY';
          // An older server sends one `test` and no list; the day then reads as having none.
          const tests = day.tests ?? [];
          return (
            <li
              key={day.date}
              data-state={day.state}
              aria-current={today ? 'date' : undefined}
              className={cn('grid grid-cols-[48px_minmax(0,1fr)_auto] items-start gap-3 border-t border-border px-2 py-3 md:grid-cols-[64px_minmax(0,1fr)_auto]', today && 'rounded-md border-transparent bg-primary/25')}
            >
              <div className="flex flex-col">
                <span className={cn('text-[13px] font-semibold', !today && 'text-foreground/80')}>
                  {formatDay(day.date, language, { weekday: 'short' })}
                </span>
                <span className="text-xs text-muted-foreground">{formatDay(day.date, language, { day: 'numeric', month: 'short' })}</span>
              </div>

              <div className="min-w-0 text-sm">
                {tests.length > 0 ? (
                  <ul className="flex flex-col gap-1">{tests.map((test) => testRow(test, today))}</ul>
                ) : day.state === 'OFF' ? (
                  <span className="text-muted-foreground" aria-hidden="true">—</span>
                ) : (
                  <span className="text-muted-foreground">{day.state === 'MISSED' ? t('studyPlan.day_missed') : t('studyPlan.day_no_test')}</span>
                )}
                {day.light && <Badge variant="secondary" className={tests.length > 0 ? 'mt-1.5' : 'ml-2'}>{t('studyPlan.day_light')}</Badge>}
              </div>

              <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
                {day.state === 'DONE' && (
                  <>
                    <span>{t('studyPlan.day_done')}</span>
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-background"><Check size={13} strokeWidth={3} aria-hidden="true" /></span>
                  </>
                )}
                {today && <span className="font-semibold text-foreground">{t('studyPlan.day_today')}</span>}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-[12.5px] text-muted-foreground">{t('studyPlan.week_note')}</p>
    </section>
  );
}

export default WeekCard;
